import { Close20, SailboatOffshore20, Search16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import _ from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User, UserStatus } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import {
  CpBody01Placeholder,
  CpBody01Primary,
  CpBody01Secondary,
  CpBody02Placeholder,
  CpBody03Primary,
} from 'components/atoms/customer-portal/typography';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import { SearchInput } from 'components/organisms/customer-portal/components/cp-input';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { CrownGold } from 'components/organisms/module-settings/team-members/list/table-components';
import { useAccount } from 'hooks/use-account';
import { useGetAllNativeUsersFromIndexedDb } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { tryEmojify } from 'utils/emojify';
import { fullName } from 'utils/full-name';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { CpSectionWrapper } from '../../common';
import { CpWidgetHeader } from '../../common/header';
import { CpTeamMemberWidgetProps } from '../cp-teamMember';
import { useSortProjectMembers } from './utils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow-y: hidden;
`;

const CardBlock = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderCardBlock = styled(CardBlock)`
  padding: 24px;
`;

const CardBlockWithBottomBorder = styled(CardBlock)`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 290px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ant-avatar-lg {
    width: 100%;
    height: 100%;
  }

  .ant-avatar-string {
    font-size: 100px;
  }
`;
const NameWithAvatar = styled.div``;
const FieldsContainer = styled.div``;
const UserCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 344px;
  /* min-height: 290px; */
  box-shadow: var(--shadow-01);
  background: white;
`;

const UserCardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--cp-spacing-06);
  width: 100%;
  ${UserCard} {
    flex-basis: calc(calc(100% / 3) - 16px);
    flex-grow: 0;
  }
  @media (max-width: 768px) {
    ${ImageContainer} {
      height: 192px;
    }
  }
  @media (max-width: 480px) {
    gap: var(--cp-spacing-03);

    ${UserCard} {
      width: 100%;
      flex-basis: 100%;
    }
    ${HeaderCardBlock} {
      padding: 0;
      gap: var(--cp-spacing-02);
    }
    ${NameWithAvatar} {
      display: flex;
      gap: var(--cp-spacing-03);
      padding: var(--cp-spacing-03);
    }
    ${CardBlockWithBottomBorder} {
      flex-direction: row;
      border: none;
      padding: 0;
    }
    ${ImageContainer} {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      .ant-avatar-string {
        font-size: var(--font-size-sm);
      }
    }
    ${FieldsContainer} {
      display: flex;
      flex-direction: column;
      gap: var(--cp-spacing-02);
      padding: var(--cp-spacing-03);
    }
  }
`;
const TimeOffIcon = styled(SailboatOffshore20)``;
const HeaderText = styled(CpBody03Primary)`
  ${CrownGold} {
    height: 20px;
    width: 20px;
  }
  ${CrownGold},
  ${TimeOffIcon} {
    margin-right: var(--cp-spacing-02);
    margin-bottom: -2px;
  }
  @media (max-width: 480px) {
    ${CrownGold},
    ${TimeOffIcon} {
      height: 16px;
      width: 16px;
      margin-bottom: -1px;
    }
  }
`;
const RoleText = styled(CpBody01Secondary)``;
const FieldNameText = styled(CpBody01Primary)`
  opacity: 0.5;
`;
const FieldValueText = styled(CpBody01Primary)``;
const EmptyStateMessage = styled(CpBody02Placeholder)`
  padding: var(--cp-spacing-08) 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SeeMore = styled(CpBody01Placeholder)`
  cursor: pointer;
  padding: var(--cp-spacing-05) 0;
  color: var(--cp-blue-50);
  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-03);
  }

  .cp-dark-background & {
    color: var(--cp-white);
  }
`;

const UserTimeOffDetails: FC<{ user: User }> = ({ user }) => {
  const users = useGetAllNativeUsersFromIndexedDb();
  const currentUser = useMemo(
    () => users.find(({ userId }) => user.userId === userId),
    [users, user.userId],
  );
  const hasTimeOffToday = checkIfCurrentDateHasTimeOff(
    currentUser?.userTimeOffDetails ?? [],
  );

  return (
    <>
      {hasTimeOffToday && currentUser != null ? (
        <Tooltip
          title={
            <GetTimeOffTooltipContent
              timeOff={hasTimeOffToday}
              userName={(currentUser as any)?.userName}
            />
          }
          placement="right"
        >
          <TimeOffIcon color="var(--cp-purple-60)" />
        </Tooltip>
      ) : (
        <></>
      )}
    </>
  );
};
const INITIAL_NUMBER_OF_MEMBERS_TO_SHOW = 6;

export const CpTeamMemberCard: React.FC<CpTeamMemberWidgetProps> = ({
  disabled,
  data,
  widget,
  projectId,
}) => {
  const { company } = useAccount();

  const background = useGetBackground(widget);
  const translate = useTranslate();
  const [search, setSearch] = useState('');
  const [numberOfMembersToShow, setNumberOMembersToShow] = useState(
    INITIAL_NUMBER_OF_MEMBERS_TO_SHOW,
  );
  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setSearch(e.target.value);
      setNumberOMembersToShow(INITIAL_NUMBER_OF_MEMBERS_TO_SHOW);
    },
    [],
  );
  const onSearchClear: React.MouseEventHandler<React.ReactSVGElement> =
    useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
      setNumberOMembersToShow(INITIAL_NUMBER_OF_MEMBERS_TO_SHOW);
    }, []);
  const { sortProjectMembers, projectOwnerId } = useSortProjectMembers({
    projectId,
  });
  const sortedMembers = useMemo(
    () => sortProjectMembers(data?.teamMembers ?? []),
    [data?.teamMembers, sortProjectMembers],
  );
  const filteredMemberData = useMemo(
    () =>
      sortedMembers?.filter(
        (user) =>
          user.company.companyId === company.companyId &&
          user.userStatus === UserStatus.Active &&
          (search.length > 0
            ? fullName(user).toLowerCase().includes(search.trim().toLowerCase())
            : true),
      ) ?? [],
    [company.companyId, search, sortedMembers],
  );
  const showMore = useCallback(() => {
    setNumberOMembersToShow((previous) =>
      Math.min(
        previous + INITIAL_NUMBER_OF_MEMBERS_TO_SHOW,
        filteredMemberData.length,
      ),
    );
  }, [filteredMemberData.length]);

  return (
    <CpSectionWrapper
      paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
      isDisabled={disabled}
      background={background}
      data-builder-mode-full-tile="true"
    >
      <Wrapper>
        <CpWidgetHeader>
          <CpWidgetHeader.Title
            data-cy="cp.team-members.cards.title"
            autoAdjustColor
            id="hero-title"
          >
            {data?.title ?? ''}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description
            data-cy="cp.team-members.cards.description"
            autoAdjustColor
          >
            {data?.description ?? ''}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
        <SearchInput
          data-cy="cp.team_members.card.search"
          prefix={<Search16 />}
          value={search}
          placeholder={translate('search')}
          onChange={onSearchChange}
          suffix={search ? <Close20 onClick={onSearchClear} /> : undefined}
        />
        {filteredMemberData?.length > 0 ? (
          <>
            <UserCardsWrapper>
              {filteredMemberData
                ?.slice(0, numberOfMembersToShow)
                ?.map((user) => (
                  <UserCard
                    data-cy={`cp.team-members.cards.user-card.${fullName(
                      user,
                    )}`}
                    key={user?.userId}
                  >
                    <NameWithAvatar>
                      <ImageContainer
                        data-cy={`cp.team-members.cards.user-card.image-container.${fullName(
                          user,
                        )}`}
                      >
                        {user?.photoUrl && user?.photoUrl !== 'random.jpg' ? (
                          <img src={user?.photoUrl} alt="user_image" />
                        ) : (
                          <Avatar
                            size="large"
                            shape="square"
                            name={fullName(user)}
                            src={user?.photoUrl}
                          />
                        )}
                      </ImageContainer>
                      <HeaderCardBlock>
                        <HeaderText
                          data-cy={`cp.team-members.cards.user-card.header-text.${fullName(
                            user,
                          )}`}
                        >
                          {projectOwnerId === user?.userId && (
                            <Tooltip
                              title={
                                <FormattedMessage id="projects.list.project_owner" />
                              }
                              placement="right"
                            >
                              <span className="icon">
                                <CrownGold />
                              </span>
                            </Tooltip>
                          )}
                          <UserTimeOffDetails user={user} />
                          {`${user?.firstName ?? '-'} ${user?.lastName ?? ''}`}
                        </HeaderText>
                        <RoleText
                          data-cy={`cp.team-members.cards.user-card.header-role-text.${fullName(
                            user,
                          )}`}
                        >
                          {user?.role?.roleName ?? '-'}
                        </RoleText>
                      </HeaderCardBlock>
                    </NameWithAvatar>
                    {(user?.fields ?? [])?.length > 0 ? (
                      <FieldsContainer>
                        {(user?.fields as any)?.map((userField) => {
                          return (
                            <CardBlockWithBottomBorder
                              data-cy={`cp.team-members.cards.user-card.field.${
                                userField?.fieldLabel
                              }.${fullName(user)}`}
                            >
                              <FieldNameText
                                data-cy={`cp.team-members.cards.user-card.field-name-text.${
                                  userField?.fieldLabel
                                }.${fullName(user)}`}
                              >
                                {userField?.fieldLabel}
                              </FieldNameText>
                              <FieldValueText
                                data-cy={`cp.team-members.cards.user-card.field-value-text.${
                                  userField?.fieldLabel
                                }.${fullName(user)}`}
                              >
                                {tryGetTextFromHTML(
                                  tryEmojify(
                                    (userField?.fieldValue as string) ?? '-',
                                  ),
                                )}
                              </FieldValueText>
                            </CardBlockWithBottomBorder>
                          );
                        })}
                      </FieldsContainer>
                    ) : (
                      <></>
                    )}
                  </UserCard>
                ))}
            </UserCardsWrapper>
            {numberOfMembersToShow < filteredMemberData.length ? (
              <SeeMore onClick={showMore}>
                <FormattedMessage id="view_more" />
              </SeeMore>
            ) : null}
          </>
        ) : (
          <EmptyStateMessage>
            <FormattedMessage id="customer_portal.team_members.empty_state.title" />
          </EmptyStateMessage>
        )}
      </Wrapper>
    </CpSectionWrapper>
  );
};
